import { type IconType } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { v4 } from 'uuid';

import {
  APP_SETUP_PAGE,
  AppPresets,
  ExtensionsSettings,
  ExtensionType,
  ModuleSettingsItem,
} from 'src/constants';
import { RouteContext } from 'src/context';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import history from 'src/history';
import { useEditModuleForm } from 'src/hooks/useEditModule';
import { useAppDispatch } from 'src/hooks/useStore';
import ContractSettingsPage from 'src/legacy/components/Contracts/ContractSettingsPage';
import FileSettingsPage from 'src/legacy/components/Files/FileSettingsPage';
import {
  useGetAppQuery,
  useGetInstallsQuery,
} from 'src/services/api/applicationsApi';
import { ContentfulApp } from 'src/services/api/contentfulApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';

import { BankingDefaultsSettings } from '../../BankingPage/BankingDefaultsSettings';
import { ExtensionViewTypeValue } from '../../ExtensionsPage/ExtensionViewType';
import MessagingPage from '../../MessagingPage';
import { ModuleContainer } from '../../ModuleContainer';
import { SettingsPageForm } from '../../SettingsPageForm';
import {
  insertModuleSettingsItem,
  moveModuleSettingsItemToFolder,
} from '../folderUtils';
import { useAppSetupPaths } from '../useAppSetupPaths';
import { useContentfulApps } from '../useContentfulApps';

import {
  ModuleFormValuesType,
  ModuleTypes,
  SetupModulePageQueryParams,
} from './appSetupTypes';
import { shouldNewExtensionBeRoot } from './resolveRootModuleId';
import SetupAppBreadCrumbs from './SetupAppBreadCrumbs';
import SetupModulePageDescription from './SetupAppPageDescription';
import { SetupCustomApp } from './SetupCustomApp';
import { useEditingApp } from './useEditingApp';

const initialValues: ModuleFormValuesType = {
  id: '',
  label: '',
  icon: 'dashboard' as IconType,
  type: ModuleTypes.Global, // for extension module
  embedLink: '', // for global extensions connected as link
  viewType: ExtensionViewTypeValue.EMBED,
  autoSizeEmbed: true,
  visibilityConfig: [],
  queryParams: [],
};

interface Query {
  appId: string;
  moduleId: string;
  moduleType: string;
  preset: string;
}

const isSetupAppQuery = (
  query: Query | Record<string, unknown>,
): query is Query => {
  return (
    'appId' in query &&
    'moduleId' in query &&
    'moduleType' in query &&
    'preset' in query
  );
};

/**
 * Returns whether a module identified by `moduleId` already has prior state we
 * should load when rendering the setup page.
 *
 * Core modules always do — their disabled/enabled state lives in moduleSettings
 * via DEFAULT_APPS, even before they are first enabled — so the Enable flow
 * for a disabled core module like Forms or Helpdesk must take this branch in
 * order to surface the module's real label and icon (POR-19701).
 *
 * Extension modules only have prior state once they are saved into
 * extensionsSettings. A pre-generated UUID from a multi-instance install click
 * is NOT yet in extensionsSettings, so it is treated as a new install just
 * like the magic string 'new'.
 */
export const isExistingModuleId = (
  moduleId: string | undefined,
  moduleType: string | undefined,
  extensionsSettings: ExtensionsSettings | undefined,
): boolean => {
  if (!moduleId || moduleId === 'new') return false;
  if (moduleType === ModuleTypes.Core) return true;
  return !!extensionsSettings?.[moduleId];
};

export const getPreset = (
  query: Query | Record<string, unknown>,
  app?: ContentfulApp,
) => {
  if (!isSetupAppQuery(query)) {
    return '';
  }

  if (app?.presetName) {
    return app.presetName;
  }

  return query.appId ? query.preset : '';
};

const SetupAppPage: React.FC = () => {
  const { allApps: contentfulApps } = useContentfulApps();
  const { query } = React.useContext(RouteContext);
  const { folderId, addAppPagePath } = useAppSetupPaths();
  const {
    data: { extensionsSettings, moduleSettings } = {
      moduleSettings: [],
      extensionsSettings: {},
    },
  } = useGetInstallsQuery();

  const {
    moduleId,
    moduleType,
    preset,
    appId: appIdParam,
  } = query as SetupModulePageQueryParams;
  // when editing an app the moduleId can be found in extensionSettings
  // to get metadata for the app. When adding a new app the moduleId is `new`
  // (or a pre-generated UUID for multi-instance apps) so we need to fallback
  // to the appId from query params
  const appId = extensionsSettings?.[moduleId]?.appId ?? appIdParam;
  const isCoreModule = moduleType === 'module';
  const isExistingModule = isExistingModuleId(
    moduleId,
    moduleType,
    extensionsSettings,
  );
  const currentEditingModule = useEditingApp();
  const dispatch = useAppDispatch();
  const modalCtx = React.useContext(AppSettingsModalContext);
  const app = contentfulApps?.find((a) => a.copilotAppId === appIdParam);
  const label = getPreset(query, app);

  /**
   * Calculate the initial editing module value.
   * For edit flows (existing module), use currentEditingModule or fallback to initialValues.
   * For add flows (moduleId === 'new' or a fresh UUID), construct initial values for a new module.
   */
  const editingModule = React.useMemo((): ModuleFormValuesType => {
    if (isExistingModule) {
      return currentEditingModule || initialValues;
    }
    // add custom module flow
    return {
      id: '',
      type:
        // custom app type will be 'authenticated'
        // since we are hiding the setup type select field
        preset === AppPresets.CustomApp
          ? ModuleTypes.Authenticated
          : ModuleTypes.Global,
      icon: 'dashboard' as IconType,
      label,
      embedCode: '',
      autoSizeEmbed: true, // for global extension, auto-size embed check should be initially true
      embedLink: '',
      viewType: ExtensionViewTypeValue.EMBED,
      visibilityConfig: [],
      queryParams: [],
    };
  }, [isExistingModule, currentEditingModule, preset, label]);

  /**
   * This function calculates the updating core module disabled state based
   * If it is disabled and it is the editing module, it should be enabled
   * (scenario: enabling a core module).
   * Otherwise, keep its disabled state (do not change)
   * @param module
   * @returns
   */
  const getUpdatingModuleDisabledStatus = useCallback(
    (module: ModuleSettingsItem) => {
      if (moduleType === 'module' && module.disabled) {
        return false;
      }

      return module.disabled;
    },
    [moduleType],
  );

  /**
   * When an extension is an authenticated extension type
   * set the type as 'global' in extension config
   * in the future we may want to also allow local extensions to be authenticated
   * @param updatedModule the updated extension module
   * @returns { type: 'global' | 'local', isAuthenticated: boolean }
   */
  const getAuthenticatedExtensionProperties = (
    updatedModule: ModuleFormValuesType,
  ) => {
    let extensionType = updatedModule.type;
    const isAuthenticated = extensionType === ModuleTypes.Authenticated;
    if (isAuthenticated) {
      extensionType = ModuleTypes.Global;
    }
    return { isAuthenticated, type: extensionType as ExtensionType };
  };

  /**
   *
   * @param updatedModule the updated extension module
   * handles both add and update scenarios.
   * The function updates both extensions and moduleSettings in redux store.
   * After successful update, it redirects to the module management page.
   */
  const handleSave = useCallback(
    async (updatedModule: ModuleFormValuesType) => {
      // edit module flow
      if (isExistingModule) {
        let updatedModuleSettings = moduleSettings.map((module) => {
          if (module.id === editingModule.id) {
            return {
              ...module,
              icon: updatedModule.icon,
              label: updatedModule.label,
              disabled: getUpdatingModuleDisabledStatus(
                module as ModuleSettingsItem,
              ),
              visibilityConfig: updatedModule.visibilityConfig,
            };
          }

          return module;
        });

        // If enabling a core module into a folder, move it to the folder position
        if (isCoreModule && editingModule.disabled && folderId) {
          updatedModuleSettings = moveModuleSettingsItemToFolder(
            updatedModuleSettings,
            editingModule.id,
            folderId,
          );
        }

        // Extension settings must be saved before module settings — the
        // backend does a read-modify-write with eventually consistent reads,
        // so the last write wins and can clobber earlier fields.
        if (!isCoreModule) {
          const updatingExtensionConfig = extensionsSettings
            ? extensionsSettings[editingModule.id]
            : null;

          if (updatingExtensionConfig) {
            const updatedExtensionSettings = {
              ...extensionsSettings,
              [editingModule.id]: {
                ...updatingExtensionConfig,
                ...(appId
                  ? { type: 'global' as const, isAuthenticated: true }
                  : getAuthenticatedExtensionProperties(updatedModule)),
                name: updatedModule.label,
                embedCode: updatedModule.embedCode,
                embedLink: updatedModule.embedLink,
                autoSizeEmbed: updatedModule.autoSizeEmbed,
                embedlyData: updatedModule.embedlyData,
                icon: updatedModule.icon,
                queryParams: updatedModule.queryParams,
              },
            };
            await dispatch(
              updateExtensionSettingsAction(updatedExtensionSettings),
            );
          }
        }
        await dispatch(updateModuleSettings(updatedModuleSettings));
        if (!modalCtx) {
          history.push(APP_SETUP_PAGE.path);
        }
        return;
      }

      // add new extension flow — use the pre-generated UUID from the URL (for
      // multi-instance apps) or generate a fresh one (for legacy 'new' flows)
      const newExtensionModuleConfigId: string =
        moduleId !== 'new' ? moduleId : v4();
      const newExtensionModule = {
        ...(appId
          ? { type: 'global' as const, isAuthenticated: true }
          : getAuthenticatedExtensionProperties(updatedModule)),
        icon: updatedModule.icon,
        name: updatedModule.label,
        embedCode: updatedModule.embedCode,
        embedLink: updatedModule.embedLink,
        autoSizeEmbed: updatedModule.autoSizeEmbed,
        embedlyData: updatedModule.embedlyData,
        appId,
        isRoot: shouldNewExtensionBeRoot(extensionsSettings, appId),
        queryParams: updatedModule.queryParams,
      };

      const moduleSettingsItem: ModuleSettingsItem = {
        id: newExtensionModuleConfigId,
        label: updatedModule.label,
        icon: updatedModule.icon,
        disabled: false,
        iuSidebarHidden: false,
        visibilityConfig: updatedModule.visibilityConfig,
        ...getAuthenticatedExtensionProperties(updatedModule),
      };

      const updatedExtensionSettings = {
        ...extensionsSettings,
        [newExtensionModuleConfigId]: newExtensionModule,
      };

      await dispatch(
        updateExtensionSettingsAction(
          updatedExtensionSettings as ExtensionsSettings,
        ),
      );
      // making new extension item part of the module settings for optimistic update
      await dispatch(
        updateModuleSettings(
          insertModuleSettingsItem(
            moduleSettings,
            moduleSettingsItem,
            folderId,
          ),
        ),
      );
      if (!modalCtx) {
        history.push(APP_SETUP_PAGE.path);
      }
    },
    [
      appId,
      editingModule,
      moduleSettings,
      extensionsSettings,
      dispatch,
      getUpdatingModuleDisabledStatus,
      isCoreModule,
      isExistingModule,
      moduleId,
      folderId,
      modalCtx,
    ],
  );

  /**
   * This function calculates the form action button label
   * For core modules, when it is disabled it should be, the form action button
   * label should be 'Enable', otherwise it should be 'Save'.
   * For extension module, when it is the add flow (moduleId='new')
   * it should be 'Add', otherwise on edit extension flow it should be 'Save'
   */
  const getFormActionLabel = React.useCallback(() => {
    if (isCoreModule) {
      return editingModule.disabled ? 'Enable' : 'Save';
    }
    return !isExistingModule ? 'Add' : 'Save';
  }, [moduleType, isExistingModule, editingModule]);

  /**
   * This function handle the cancel action when it is an add module flow.
   * if it is an add/enable module flow, it should redirect to module management page.
   */
  const handleCancel = () => {
    // cancel add extension or enable core module should redirect to add module page
    if (!isExistingModule || editingModule.disabled) {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(addAppPagePath);
      }
    }
  };

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  // messages, files, billings and contracts module
  // have separate components for setting page
  if (moduleType === 'module') {
    if (moduleId === 'files') {
      return <FileSettingsPage />;
    }

    if (moduleId === 'messages') {
      return <MessagingPage />;
    }

    if (moduleId === 'payments') {
      return <BankingDefaultsSettings />;
    }

    if (moduleId === 'contracts') {
      return <ContractSettingsPage />;
    }
  }

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <SettingsPageForm<ModuleFormValuesType>
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        initialFormValue={editingModule}
        useFormHook={useEditModuleForm}
        handleSave={handleSave}
        actionLabel={getFormActionLabel()}
        validateOnMount={moduleId === 'new'}
        // on add extension flow, or enable core module flow we should always show the save bar
        shouldShowPageAction={!isExistingModule || editingModule.disabled}
        handleCancel={handleCancel}
        showBackButton={isSm}
        onBackButtonClick={modalCtx?.onClose ?? (() => history.goBack())}
        enableDefaultErrorTooltip
      />
    </ModuleContainer>
  );
};

export default () => {
  const { query } = React.useContext(RouteContext);
  const { preset, appId } = query as SetupModulePageQueryParams;
  // If app ID exists and we don't get a successful response from this query
  // that means it's a marketplace app installed in a portal it did not originate from.
  const { isSuccess, isLoading } = useGetAppQuery(
    { id: appId },
    {
      skip: !appId,
    },
  );

  if (isLoading) return null;

  if (preset === 'custom' || isSuccess) {
    return <SetupCustomApp />;
  }

  return <SetupAppPage />;
};
